import { type DevString, type Handler, Outcome } from "@phosphor/utils";
import type { Atom } from "jotai";
import { type UID, World, type WorldState } from "#scripts/vmc/WorldState.ts";

export type Provider<T, R = void> = (options: R) => null | [DevString, T];

export class CParent extends World.Component("parent")<
  CParent,
  {
    uidAtom: Atom<UID | null>;
  }
>() {
  static dispatch<T>(
    setupReason: DevString,
    world: WorldState,
    start: UID,
    handlerComponent: World.AnyComponentClass<{ handler: Handler<T> }>,
    eventReason: DevString,
    event: T,
    { includeStart = true } = {},
  ): Outcome {
    const iterate = (current: UID): Outcome => {
      const handler = world.getComponentMaybe(current, handlerComponent);
      if (handler) {
        const result = handler.handler(eventReason, event);
        if (result !== Outcome.Passthrough) return result;
      }
      const parent = world.getComponentMaybe(current, CParent);
      if (!parent) return Outcome.Passthrough;
      const parentUID = world.store.get(parent.uidAtom);
      if (!parentUID) return Outcome.Passthrough;
      return iterate(parentUID);
    };
    return iterate(start);
  }

  static findFirst<T extends World.AnyComponentClass>(
    reason: DevString,
    world: WorldState,
    start: UID,
    targetComponent: T,
    { includeStart = true } = {},
  ): World.EntityBundle<[T]> | null {
    const iterate = (current: UID): World.EntityBundle<[T]> | null => {
      const entity = world.getEntityMaybe(current, [targetComponent]);
      if (entity) return entity;
      const parent = world.getComponentMaybe(current, CParent);
      if (!parent) return null;
      const parentUID = world.store.get(parent.uidAtom);
      if (!parentUID) return null;
      return iterate(parentUID);
    };
    return iterate(start);
  }

  static collect<R, T>(
    reason: DevString,
    world: WorldState,
    start: UID,
    providerComponent: World.AnyComponentClass<{ provider: Provider<T, R> }>,
    providerOptions: NoInfer<R>,
  ): [DevString, T][] {
    const collected: [DevString, T][] = [];
    const iterate = (current: UID): void => {
      const provider = world.getComponentMaybe(current, providerComponent);
      if (provider) {
        const result = provider.provider(providerOptions);
        if (result) collected.push(result);
      }
      const parent = world.getComponentMaybe(current, CParent);
      if (!parent) return;
      const parentUID = world.store.get(parent.uidAtom);
      if (!parentUID) return;
      iterate(parentUID);
    };
    iterate(start);
    return collected;
  }
}
